import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause } from '@phosphor-icons/react';

const AutoReadingFace = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [isHovered, setIsHovered] = useState(false);
  const utteranceRef = useRef(null);

  // Check if speech synthesis is available
  const isSpeechAvailable = typeof window !== 'undefined' && 'speechSynthesis' in window;

  useEffect(() => {
    if (!isSpeechAvailable) return;

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isSpeechAvailable]);

  const handlePlay = () => {
    if (!isSpeechAvailable) return;

    // Get visible text from the current viewport
    const mainContent = document.querySelector('[data-testid="home-page"]');
    if (!mainContent) return;

    const textContent = mainContent.innerText.substring(0, 500); // Read first 500 chars

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(textContent);
    utterance.rate = speed;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onend = () => {
      setIsPlaying(false);
    };

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setIsPlaying(true);
  };

  const handlePause = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
  };

  const cycleSpeed = () => {
    const speeds = [0.75, 1, 1.25, 1.5];
    const currentIndex = speeds.indexOf(speed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setSpeed(nextSpeed);
  };

  if (!isSpeechAvailable) return null;

  return (
    <motion.div
      className="fixed bottom-6 left-6 z-40"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1 }}
    >
      <motion.div
        animate={{ width: isHovered ? 200 : 60, height: isHovered ? 60 : 60 }}
        transition={{ duration: 0.3 }}
        className="glass-surface border border-[#00F0FF]/30 rounded-full flex items-center gap-2 p-2 shadow-xl overflow-hidden"
      >
        {/* Oracle Avatar */}
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF00FF] flex items-center justify-center text-2xl flex-shrink-0"
        >
          🔮
        </motion.div>

        {/* Controls (visible on hover) */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="flex items-center gap-2"
            >
              <button
                onClick={isPlaying ? handlePause : handlePlay}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <Pause size={20} className="text-[#00F0FF]" weight="fill" />
                ) : (
                  <Play size={20} className="text-[#00F0FF]" weight="fill" />
                )}
              </button>

              <button
                onClick={cycleSpeed}
                className="px-2 py-1 text-xs font-bold text-[#00F0FF] hover:bg-white/10 rounded transition-colors"
                title="Change speed"
              >
                {speed}x
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Status indicator */}
      {isPlaying && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#00FFA3] animate-pulse border-2 border-[#0B0E14]"
        />
      )}
    </motion.div>
  );
};

export default AutoReadingFace;
